// Caché local de mensajes ENVIADOS por el usuario logueado.
//
// Problema: el endpoint GET /api/message/list/:id del backend devuelve solo
// los mensajes RECIBIDOS por ese id. No hay endpoint para "mis enviados".
// MessagingHub mantiene los enviados en localStorage para que los chats
// muestren el historial completo (yo ↔ otro) entre dispositivos al recargar.
//
// Este módulo expone una API mínima para que cualquier sitio (modales de
// asignación de vacaciones, edición de periodos, etc.) que use `sendMessage`
// directamente pueda meter el resultado en la misma caché. Así el admin
// también ve el mensaje automático en su chat con el trabajador.
//
// La caché se rehidrata sola en MessagingHub al recibir el evento custom
// `tc:sent-message-appended`. Ese evento solo viaja en la misma pestaña;
// para sincronización entre pestañas, MessagingHub también escucha el
// evento `storage` nativo del navegador.

const KEY_PREFIX = 'tc.sent.';
const SENT_CAP = 500;
const EVENT_NAME = 'tc:sent-message-appended';

const storageKey = (userId) => (userId ? `${KEY_PREFIX}${userId}` : null);

// Lee la lista de enviados del usuario. Defensiva frente a JSON corrupto
// o navegadores con localStorage bloqueado.
export const readSent = (userId) => {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeSent = (userId, list) => {
  const key = storageKey(userId);
  if (!key || typeof window === 'undefined') return;
  try {
    const trimmed = list.length > SENT_CAP ? list.slice(-SENT_CAP) : list;
    window.localStorage.setItem(key, JSON.stringify(trimmed));
  } catch {
    // localStorage lleno o bloqueado: silenciamos. No es crítico — el
    // mensaje ya está persistido en el backend, solo afecta a la vista
    // local del remitente.
  }
};

// Añade un (o varios) mensajes enviados a la caché del usuario y dispara
// el evento custom para que MessagingHub se refresque sin reload.
//
// Cada `created` debe traer al menos `id`, `senderId`, `receiverId`,
// `content`, `timestamp` (el backend los devuelve al hacer POST). Si
// alguno falta, lo rellenamos con defaults razonables para no romper la
// UI del chat.
export const appendSentMessage = (userId, created) => {
  if (!userId || !created) return;
  const arr = Array.isArray(created) ? created : [created];
  const prev = readSent(userId);
  const seen = new Set(prev.map((m) => m.id));
  const fresh = arr
    .filter((m) => m && m.id != null && !seen.has(m.id))
    .map((m) => ({
      // Defaults defensivos: el backend incluye estos campos pero por
      // seguridad asumimos PENDING/false/now si faltan.
      read: false,
      timestamp: m.timestamp || new Date().toISOString(),
      ...m,
    }));
  if (fresh.length === 0) return;
  const next = [...prev, ...fresh];
  writeSent(userId, next);
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { userId } }));
    } catch {
      // Edge cases muy antiguos sin CustomEvent. Ignoramos.
    }
  }
};

// Suscribe a cambios en la caché de un usuario concreto. Devuelve la
// función de desuscripción. Cubre dos casos:
//   1) Misma pestaña: evento custom disparado por appendSentMessage.
//   2) Otra pestaña: evento `storage` del navegador cuando se reescribe
//      localStorage.
export const subscribeToSent = (userId, callback) => {
  if (!userId || typeof window === 'undefined') return () => {};
  const key = storageKey(userId);
  const onCustom = (e) => {
    if (e?.detail?.userId === userId) callback();
  };
  const onStorage = (e) => {
    if (e.key === key) callback();
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener('storage', onStorage);
  };
};
